import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import clsx from 'clsx';

import { useResponsive } from '@/hooks/useResponsive';
import { useDownloadInfo } from '@/pages/Download/hooks/useDownloadInfo';
import { useBannerStore } from '@/store/useBannerStore';
import { openDownloadInNewWindow, safeDownload } from '@/utils/downloadHelper';
import {
  getRecommendedDownloadType,
  getSystemInfo,
  isSystemSupported,
} from '@/utils/systemDetector';

import previewLogo from './PREVIEW-logo.png';
import css from './index.module.less';
import previewLogoCN from './preview-logo-cn.png';

export function NavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { downloadInfo } = useDownloadInfo();
  const [recommendedType, setRecommendedType] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentLogo, setCurrentLogo] = useState(previewLogo);
  const { isBannerVisible } = useBannerStore();

  // 响应式状态管理
  const { isMobile } = useResponsive();

  // 动态高度测量（仅移动端）
  const [bannerHeight, setBannerHeight] = useState(0);
  const navFixedRef = useRef<HTMLDivElement | null>(null);
  const measureHeights = useCallback(() => {
    const bannerEl = document.getElementById('global-banner');
    const newBannerHeight = bannerEl ? bannerEl.offsetHeight : 0;
    setBannerHeight(newBannerHeight || 0);
  }, []);

  // 提前到布局阶段进行首次测量，避免首屏抖动（仅在移动端执行动态测量）
  useLayoutEffect(() => {
    if (!isBannerVisible) {
      setBannerHeight(0);
      return;
    }

    if (!isMobile) {
      // 桌面端：Banner 高度固定 32px，无需动态测量
      setBannerHeight(32);
      return;
    }

    // 移动端：进行实时测量
    measureHeights();
    const bannerEl = document.getElementById('global-banner');

    let ro: ResizeObserver | null = null;
    if (bannerEl && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => measureHeights());
      ro.observe(bannerEl);
    }

    const handleResize = () => measureHeights();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (ro && bannerEl) ro.unobserve(bannerEl);
    };
  }, [isMobile, isBannerVisible, measureHeights]);

  // 根据当前语言选择合适的 logo
  const getCurrentLogo = useCallback(() => {
    const currentLanguage = i18n.language;
    const isChinese = ['zh', 'zh-CN', 'zh-Hans', 'zh-Hant', 'zh-TW', 'zh-HK'].some((lang) =>
      currentLanguage.toLowerCase().startsWith(lang.toLowerCase()),
    );
    return isChinese ? previewLogoCN : previewLogo;
  }, [i18n.language]);

  // 监听语言变化，更新 logo
  useEffect(() => {
    const updateLogo = () => {
      setCurrentLogo(getCurrentLogo());
    };

    // 初始化时设置 logo
    updateLogo();

    // 监听语言变化
    i18n.on('languageChanged', updateLogo);

    return () => {
      i18n.off('languageChanged', updateLogo);
    };
  }, [getCurrentLogo, i18n]);

  // 滚动监听 - 首页时根据滚动位置显示背景色，其他页面始终显示背景色
  useEffect(() => {
    const handleScroll = () => {
      if (location.pathname === '/') {
        const scrollTop = document.documentElement.scrollTop;
        const heroTitleElement = document.getElementById('hero-title');

        // 动态获取 navbar 可见高度
        const navFixedEl = navFixedRef.current;
        const NAVBAR_HEIGHT = navFixedEl ? navFixedEl.offsetHeight : 86;
        const BANNER_HEIGHT = isBannerVisible ? (isMobile ? bannerHeight : 32) : 0;

        if (heroTitleElement) {
          const heroTitleRect = heroTitleElement.getBoundingClientRect();
          const heroTitleTop = heroTitleRect.top + scrollTop;
          // 当滚动到 hero-title 上方 12px（考虑 banner 与 navbar 高度）后，显示背景
          const threshold = heroTitleTop - 12 - NAVBAR_HEIGHT - BANNER_HEIGHT;
          setIsScrolled(scrollTop > threshold);
        } else {
          // 找不到 hero-title 时：兜底为超出一屏高度后显示
          const windowHeight = window.innerHeight;
          setIsScrolled(scrollTop > windowHeight);
        }
      } else {
        // 其他页面：始终显示背景色
        setIsScrolled(true);
      }
    };

    // 添加滚动事件监听
    window.addEventListener('scroll', handleScroll, { passive: true });

    // 初始检查
    handleScroll();

    // 清理函数
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname, isBannerVisible, bannerHeight, isMobile]);

  // 初始化时检测用户系统
  useEffect(() => {
    const systemInfo = getSystemInfo();
    const recommendedDownloadType = getRecommendedDownloadType();
    setRecommendedType(recommendedDownloadType);

    // 在开发环境下输出调试信息
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('NavBar 检测到的系统信息:', systemInfo);
      // eslint-disable-next-line no-console
      console.log('NavBar 推荐下载类型:', recommendedDownloadType);
    }
  }, []);

  function handleHome() {
    navigate('/');
  }

  // 智能下载处理函数

  // 智能下载处理函数
  const handleDownload = useCallback(async () => {
    if (!downloadInfo || !recommendedType) {
      // 如果没有下载信息或无法检测系统，跳转到下载页面
      navigate('/download');
      return;
    }

    let targetDownloadInfo = null;

    // 根据推荐类型获取下载信息
    switch (recommendedType) {
      case 'apple':
        targetDownloadInfo = downloadInfo.arm;
        break;
      case 'intel':
        targetDownloadInfo = downloadInfo.intel;
        break;
      case 'windows':
        targetDownloadInfo = downloadInfo.windows;
        break;
      default:
        // 不支持的系统，跳转到下载页面
        navigate('/download');
        return;
    }

    if (targetDownloadInfo) {
      try {
        // 尝试安全下载
        const downloadSuccess = await safeDownload(targetDownloadInfo);

        if (!downloadSuccess) {
          // 如果下载失败，尝试在新窗口打开
          const downloadUrl = targetDownloadInfo.dmgUrl || targetDownloadInfo.url;
          if (downloadUrl) {
            openDownloadInNewWindow(downloadUrl);
          } else {
            // 最后的备用方案：跳转到下载页面
            navigate('/download');
          }
        }
      } catch (error) {
        console.error('NavBar 下载失败:', error);
        // 出错时跳转到下载页面
        navigate('/download');
      }
    } else {
      // 没有下载信息，跳转到下载页面
      navigate('/download');
    }
  }, [downloadInfo, recommendedType, navigate]);

  // 处理不支持系统的点击事件
  const handleUnsupportedClick = useCallback(() => {
    navigate('/download');
  }, [navigate]);

  // 检查是否支持当前系统
  const systemSupported = isSystemSupported();

  return (
    <>
      <div
        className={clsx(css['nav-bar'], isBannerVisible && css['nav-bar--with-banner'])}
        style={{ top: isMobile && isBannerVisible ? bannerHeight : undefined }}
      >
        <div
          ref={navFixedRef}
          className={clsx(css['nav-bar__fixed'], isScrolled && css['nav-bar__fixed--scrolled'])}
        >
          <div className={clsx(css['nav-bar-container'])}>
            <div className={clsx(css['logo'])} onClick={handleHome}>
              <img className={clsx(css['logo-icon'])} src={currentLogo} alt="logo" />
            </div>

            {/* 桌面端导航 */}
            {!isMobile && (
              <div
                className={clsx(
                  css['download-btn'],
                  !systemSupported && css['download-btn--disabled'],
                )}
                onClick={systemSupported ? handleDownload : handleUnsupportedClick}
                title={
                  systemSupported
                    ? t('navbar.downloadTooltipSupported')
                    : t('navbar.downloadTooltipUnsupported')
                }
              >
                <span className={clsx(css['download-text'])}>{t('navbar.download')}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
